/**
 * CastButton — unified Cast (Chromecast) + AirPlay button.
 *
 *   • On Chrome/Edge desktop & Android, mounts a Google Cast picker.
 *   • On Safari (macOS + iOS), mounts the native AirPlay picker via
 *     `video.webkitShowPlaybackTargetPicker()`.
 *   • Hides itself entirely when no Cast device is available AND
 *     AirPlay isn't supported — never a dead button.
 *
 *  Props:
 *    media       – { url, contentType, title, subtitle, poster }.
 *                  `url` is required for Cast (the receiver streams
 *                  this URL directly from the CDN — the phone is just
 *                  the remote control). YouTube iframes can't be
 *                  Cast through this path; the button stays hidden.
 *    videoRef    – ref to the underlying <video> element. Required
 *                  for AirPlay (the native picker is wired to it).
 *    className   – passthrough Tailwind classes for the button.
 *    testid      – data-testid for automated testing.
 */
import React, { useEffect, useState } from "react";
import { MonitorPlay, AppleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import useCast from "../hooks/useCast";

export default function CastButton({
  media,
  videoRef,
  className = "",
  testid = "cast-button",
  variant = "icon", // "icon" (small round) | "pill" (labeled "Cast to TV")
}) {
  const { available, casting, cast, stop } = useCast();
  const [airplayAvailable, setAirplayAvailable] = useState(false);

  // AirPlay availability is reported by Safari via a per-video
  // `webkitplaybacktargetavailabilitychanged` event. We listen for
  // it and store the result in state. Non-Safari browsers never fire
  // the event, so `airplayAvailable` stays false on Chrome/Firefox.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const v = videoRef && videoRef.current;
    if (!v) return;
    if (typeof v.webkitShowPlaybackTargetPicker !== "function") return;
    const onChange = (e) => setAirplayAvailable(e.availability === "available");
    try {
      v.addEventListener("webkitplaybacktargetavailabilitychanged", onChange);
    } catch (_) {}
    return () => {
      try {
        v.removeEventListener("webkitplaybacktargetavailabilitychanged", onChange);
      } catch (_) {}
    };
  }, [videoRef]);

  const handleCast = async () => {
    if (casting) {
      await stop();
      toast.success("Cast stopped");
      return;
    }
    // YouTube-embed media has no direct URL we can cast. Instead of
    // silently doing nothing, we coach the user to mirror their full
    // phone/laptop screen to their TV — which works for YouTube
    // embeds (the screen-mirror picks up whatever's on screen).
    // Detection: Android → Google Home / Quick Settings "Screen Cast";
    // iOS/macOS → Control Center "Screen Mirroring"; Chrome desktop →
    // menu → Cast → Sources → Cast desktop.
    if (!media?.url) {
      const ua = (navigator.userAgent || "").toLowerCase();
      const isIOS = /iphone|ipad|ipod/.test(ua);
      const isMac = /macintosh/.test(ua);
      const isAndroid = /android/.test(ua);
      let how;
      if (isIOS || isMac) {
        how = "Open Control Center → tap Screen Mirroring → pick your AirPlay TV. Holy Words will show on the big screen.";
      } else if (isAndroid) {
        how = "Pull down Quick Settings → tap Screen Cast (or Google Home → Cast). Holy Words will appear on your TV.";
      } else {
        how = "In Chrome, click the three-dot menu → Cast… → Sources → Cast desktop. Holy Words will appear on your TV.";
      }
      toast.message("Cast Holy Words to your TV", {
        description: how,
        duration: 10000,
        action: {
          label: "Full guide",
          onClick: () => { try { window.location.href = "/help/cast"; } catch { /* noop */ } },
        },
      });
      return;
    }
    // Detect no-device case → guide the user instead of silently failing.
    if (!available) {
      // Cast SDK not loaded OR no device on network.
      toast.info(
        "No Chromecast found. Make sure your TV's on the same Wi-Fi and Chrome's Cast extension is enabled.",
        { duration: 5000 }
      );
      return;
    }
    try {
      await cast(media);
      toast.success(`Casting to TV: ${media.title || "track"}`);
    } catch (e) {
      // user cancelled the picker — silent
      const msg = (e && e.message) || "";
      if (!/cancel/i.test(msg)) toast.error("Couldn't cast — try again");
    }
  };

  const handleAirPlay = () => {
    const v = videoRef && videoRef.current;
    if (!v) return;
    try { v.webkitShowPlaybackTargetPicker(); } catch (_) {}
  };

  // Cast button policy (Feb 2026): always render the button as long
  // as we're in a player. When the media is a YouTube embed (no
  // direct URL), tapping it shows a "How to mirror your screen to
  // your TV" toast instead of trying to cast a URL we don't own.
  // This gives users an obvious affordance on every player instead
  // of a button that quietly vanishes on YouTube content.
  const hasCastableMedia = !!media?.url;
  const showCast = true; // always — handler routes by media type
  // Show AirPlay button independently — only when Safari has told us
  // an AirPlay target is on the network.
  const showAirPlay = airplayAvailable;

  if (!showCast && !showAirPlay) return null;

  // Labeled pill variant — used in player footers so users plainly
  // see "Cast to TV" rather than guessing what a tiny monitor icon
  // does. Falls back to the round-icon variant if `variant="icon"`.
  // Styling (Feb 2026): user asked us to drop the green/orange pill
  // backgrounds in favor of clean text+icon affordances. The button
  // now reads as a clickable label in forest-green; the "Casting"
  // active state stays terracotta so it's still visually obvious.
  if (variant === "pill") {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        {showCast && (
          <button
            type="button"
            onClick={handleCast}
            data-testid={testid}
            aria-label={casting ? "Stop casting" : "Cast to TV"}
            title={casting ? "Stop casting" : "Cast to TV (Chromecast)"}
            className={`inline-flex items-center gap-1.5 px-2 h-10 text-xs tracking-wider uppercase font-bold transition-colors ${
              casting
                ? "text-terracotta hover:text-terracotta/80"
                : "text-forest hover:text-terracotta"
            }`}
          >
            <MonitorPlay size={14} weight={casting ? "fill" : "bold"} />
            {casting ? "Casting" : "Cast to TV"}
          </button>
        )}
        {showAirPlay && (
          <button
            type="button"
            onClick={handleAirPlay}
            data-testid={`${testid}-airplay`}
            aria-label="AirPlay"
            title="AirPlay to Apple TV"
            className="inline-flex items-center gap-1.5 px-2 h-10 text-xs tracking-wider uppercase font-bold text-forest hover:text-terracotta transition-colors"
          >
            <AppleLogo size={14} weight="bold" /> AirPlay
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      {showCast && (
        <button
          type="button"
          onClick={handleCast}
          data-testid={testid}
          aria-label={casting ? "Stop casting" : "Cast to TV"}
          title={casting ? "Stop casting" : "Cast to TV (Chromecast)"}
          className={`w-9 h-9 rounded-full grid place-items-center transition-all ${
            casting
              ? "bg-terracotta text-cream ring-2 ring-terracotta/40"
              : "bg-cream/15 text-cream hover:bg-cream/30"
          }`}
        >
          <MonitorPlay size={16} weight={casting ? "fill" : "bold"} />
        </button>
      )}
      {showAirPlay && (
        <button
          type="button"
          onClick={handleAirPlay}
          data-testid={`${testid}-airplay`}
          aria-label="AirPlay"
          title="AirPlay to Apple TV"
          className="w-9 h-9 rounded-full grid place-items-center bg-cream/15 text-cream hover:bg-cream/30 transition-all"
        >
          <AppleLogo size={16} weight="bold" />
        </button>
      )}
    </div>
  );
}
